import { LeftPanel } from "@/components/left-panel";
import { MainContent } from "@/components/main-content";
import { TopHeader } from "@/components/top-header";
import ErrorBoundary from "@/components/ErrorBoundary";
import React, { useState, useEffect, useCallback } from "react";
import { PayrollDocumentsProvider } from "@/components/payroll/PayrollDocumentsContext";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useEngagement } from "@/contexts/EngagementContext";
import { EngagementService } from "@/services/engagement-service";
import { ArchivalModeProvider, useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ExecutionEngagementDetailsProvider } from "@/contexts/ExecutionEngagementDetailsContext";
import { Engagement } from "@/types/engagement";

// Global click interceptor to block all button clicks in archival mode
function GlobalClickInterceptor({ children }: { children: React.ReactNode }) {
  const { isArchivalMode } = useArchivalMode();

  React.useEffect(() => {
    if (!isArchivalMode) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // First, check if the target or any parent is a TabsTrigger or inside Tabs component
      // This allows clicks on spans and other child elements inside TabsTrigger
      let checkElement: HTMLElement | null = target;
      while (checkElement && checkElement !== document.body) {
        const dataSlot = checkElement.getAttribute('data-slot');
        if (dataSlot === 'tabs-trigger' || dataSlot === 'tabs-list' || dataSlot === 'tabs') {
          // This is a tabs-related element, allow navigation
          return;
        }
        // Check if element is inside a TabsList by looking for the container
        const checkCls = checkElement.getAttribute("class") ?? "";
        if (checkElement.classList.contains('rounded-lg') &&
          checkCls.includes('bg-white/5') &&
          checkElement.querySelector('[data-slot="tabs-trigger"]')) {
          return;
        }
        checkElement = checkElement.parentElement;
      }

      // Check if the click is on a button or inside a button
      const button = target.closest('button');
      if (button) {
        // Allow buttons with data-allow-archival-edit attribute
        const allowInArchival = button.getAttribute('data-allow-archival-edit') === 'true';
        if (allowInArchival) {
          // This button is allowed in archival mode, don't block it
          return;
        }

        // Allow TabsTrigger buttons for navigation between tabs in archival mode
        // TabsTrigger buttons have data-slot="tabs-trigger" attribute
        const isTabsTrigger = button.getAttribute('data-slot') === 'tabs-trigger';
        if (isTabsTrigger) {
          // Allow tab navigation in archival mode
          return;
        }

        // Don't block left panel navigation buttons - check if button is in left panel
        // The left panel container has border-r and bg-black classes
        let element: HTMLElement | null = target;
        while (element && element !== document.body) {
          const classList = element.classList;
          const cls = element.getAttribute("class") ?? "";
          // Check for left panel container - look for the main left panel div
          // Left panel has: "flex h-screen min-h-0 border-r border-white/10 bg-black/60"
          if (
            (classList.contains('border-r') && (cls.includes('bg-black') || cls.includes('bg-black/60'))) ||
            classList.contains('w-16') || // Icon strip width
            (cls.includes('border-r') && cls.includes('border-white'))
          ) {
            // This is in the left panel, allow navigation
            return;
          }
          element = element.parentElement;
        }

        // Block only truly disabled buttons.
        // NOTE: Tailwind utility tokens like "disabled:pointer-events-none" are static class names
        // and may exist even when a button is enabled.
        if (button.disabled || button.getAttribute("aria-disabled") === "true") {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }

        // Allow native buttons (not Button components) - these are for navigation
        // The Button component already handles its own disabling logic
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mousedown', handleClick, true);
    document.addEventListener('mouseup', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mousedown', handleClick, true);
      document.removeEventListener('mouseup', handleClick, true);
    };
  }, [isArchivalMode]);

  return <>{children}</>;
}

export default function HomePage() {
  const { engagementId } = useParams({ from: "/engagement/$engagementId" });
  const { currentEngagement, setCurrentEngagement } = useEngagement();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSectionState] = useState("chat");
  const [navigationHistory, setNavigationHistory] = useState<string[]>(["chat"]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Enhanced setActiveSection that tracks history
  const setActiveSection = useCallback((section: string) => {
    // Always update the active section state immediately
    setActiveSectionState(section);
    
    // Then update history
    setNavigationHistory((prev) => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Only add if it's different from the current section
      if (newHistory[newHistory.length - 1] !== section) {
        newHistory.push(section);
        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      }
      return prev;
    });
  }, [historyIndex]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousSection = navigationHistory[newIndex];
      setHistoryIndex(newIndex);
      setActiveSectionState(previousSection);
    }
  }, [historyIndex, navigationHistory]);

  const canGoBack = historyIndex > 0;

  // Load engagement when component mounts or engagementId changes
  useEffect(() => {
    const loadEngagement = async () => {
      if (!engagementId) return;

      // First, check if engagement is already set in context (from EngagementHomePage)
      if (currentEngagement && currentEngagement.id === engagementId) {
        console.log('Engagement already loaded in context:', currentEngagement.id);
        return;
      }

      // Try to load from API
      try {
        const { apiService } = await import('@/services/api-service');
        const apiEngagement = await apiService.getEngagement(engagementId);

        // Convert API engagement to local format
        const localEngagement: Engagement = {
          id: apiEngagement.id,
          clientId: apiEngagement.audit_client_id,
          clientName: apiEngagement.client_name || 'Unknown Client',
          engagementName: apiEngagement.engagement_name || `Engagement ${apiEngagement.id.substring(0, 8)}`,
          typeOfEngagement: 'Financial statement audit', // Default
          financialReportingFramework: undefined,
          reportingDate: undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        setCurrentEngagement(localEngagement);
        console.log('Engagement loaded from API:', localEngagement.id);
      } catch (apiError) {
        console.warn('Failed to load engagement from API, trying localStorage:', apiError);

        // Fallback to localStorage
        const engagement = EngagementService.getEngagementById(engagementId);
        if (engagement) {
          setCurrentEngagement(engagement);
          console.log('Engagement loaded from localStorage:', engagement.id);
        } else {
          // Engagement not found anywhere, redirect to home
          console.error('Engagement not found:', engagementId);
          navigate({ to: "/" });
        }
      }
    };

    loadEngagement();
  }, [engagementId, currentEngagement, setCurrentEngagement, navigate]);

  return (
    <ErrorBoundary>
      <ArchivalModeProvider>
        <GlobalClickInterceptor>
          <HomePageContent
            engagementId={engagementId}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            canGoBack={canGoBack}
            handleBack={handleBack}
          />
        </GlobalClickInterceptor>
      </ArchivalModeProvider>
    </ErrorBoundary>
  );
}

function HomePageContent({
  engagementId,
  searchTerm,
  setSearchTerm,
  activeSection,
  setActiveSection,
  canGoBack,
  handleBack,
}: {
  engagementId: string | undefined;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
  canGoBack: boolean;
  handleBack: () => void;
}) {
  const { isArchivalMode } = useArchivalMode();

  return (
    <div className="bg-gradient-key flex h-screen flex-col">
      <TopHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onToggleLibraries={() => setActiveSection("libraries")}
      />
      <div className="flex flex-1 overflow-hidden">
        <PayrollDocumentsProvider>
          <ExecutionEngagementDetailsProvider engagementId={engagementId} activeSection={activeSection}>
            <LeftPanel
              activeSection={activeSection}
              setActiveSection={setActiveSection}
            />
            <MainContent
              activeSection={activeSection}
              searchTerm={searchTerm}
              setActiveSection={setActiveSection}
              onBack={canGoBack ? handleBack : undefined}
            />
          </ExecutionEngagementDetailsProvider>
        </PayrollDocumentsProvider>
      </div>

    </div>
  );
}
